import type {
    PublicActionItem,
    PublicActionItemEvent,
} from '@/backend/contracts/action-items-client.ts';

import type { ProjectScope } from './project-scope';

export interface ActionItemProjection {
    items: PublicActionItem[];
    events: PublicActionItemEvent[];
    reviewedEvidenceKeys: string[];
}

export interface PendingActionItemMutation {
    operationId: string;
    optimistic: ActionItemProjection;
    prior: ActionItemProjection;
    acknowledgedItemIds: string[];
    acknowledgedEventIds: string[];
    acknowledgedEvidenceKeys: string[];
}

const pendingActionItemMutations = new Map<string, PendingActionItemMutation>();

function mutationKey(scope: ProjectScope, operationId: string): string {
    return `${scope.orgId}\0${scope.projectId}\0${operationId}`;
}

function cloneProjection(projection: ActionItemProjection): ActionItemProjection {
    return {
        items: [...projection.items],
        events: [...projection.events],
        reviewedEvidenceKeys: [...projection.reviewedEvidenceKeys],
    };
}

function cloneMutation(mutation: PendingActionItemMutation): PendingActionItemMutation {
    return {
        ...mutation,
        optimistic: cloneProjection(mutation.optimistic),
        prior: cloneProjection(mutation.prior),
        acknowledgedItemIds: [...mutation.acknowledgedItemIds],
        acknowledgedEventIds: [...mutation.acknowledgedEventIds],
        acknowledgedEvidenceKeys: [...mutation.acknowledgedEvidenceKeys],
    };
}

export function markPendingActionItemMutation(scope: ProjectScope, mutation: PendingActionItemMutation): void {
    pendingActionItemMutations.set(mutationKey(scope, mutation.operationId), cloneMutation(mutation));
}

export function listPendingActionItemMutations(scope: ProjectScope): PendingActionItemMutation[] {
    const prefix = `${scope.orgId}\0${scope.projectId}\0`;
    return [...pendingActionItemMutations.entries()]
        .filter(([key]) => key.startsWith(prefix))
        .map(([, mutation]) => cloneMutation(mutation));
}

export function clearPendingActionItemMutation(scope: ProjectScope, operationId: string): void {
    pendingActionItemMutations.delete(mutationKey(scope, operationId));
}

/**
 * Reverses one local projection without overwriting rows owned by a later
 * mutation. The caller owns updating its store and clearing the mutation.
 */
export function rollbackPendingActionItemMutation(
    scope: ProjectScope,
    operationId: string,
    current: ActionItemProjection,
): ActionItemProjection | undefined {
    const pendingMutations = listPendingActionItemMutations(scope);
    const mutationIndex = pendingMutations.findIndex(entry => entry.operationId === operationId);
    const mutation = pendingMutations[mutationIndex];
    if (!mutation) {
        return;
    }

    const laterMutations = pendingMutations.slice(mutationIndex + 1);
    const restore = <T extends { id: string }>(
        rows: readonly T[],
        optimistic: readonly T[],
        prior: readonly T[],
        laterOptimistic: readonly T[],
    ): T[] => {
        const optimisticIds = new Set(optimistic.map(row => row.id));
        const laterOptimisticIds = new Set(laterOptimistic.map(row => row.id));
        const retained = rows.filter(row => !optimisticIds.has(row.id) || laterOptimisticIds.has(row.id));
        return [...retained, ...prior.filter(row => !laterOptimisticIds.has(row.id))];
    };

    return {
        items: restore(current.items, mutation.optimistic.items, mutation.prior.items, laterMutations.flatMap(entry => entry.optimistic.items)),
        events: restore(current.events, mutation.optimistic.events, mutation.prior.events, laterMutations.flatMap(entry => entry.optimistic.events)),
        reviewedEvidenceKeys: restoreKeys(
            current.reviewedEvidenceKeys,
            mutation.optimistic.reviewedEvidenceKeys,
            mutation.prior.reviewedEvidenceKeys,
            laterMutations.flatMap(entry => entry.optimistic.reviewedEvidenceKeys),
        ),
    };
}

function restoreKeys(
    current: readonly string[],
    optimistic: readonly string[],
    prior: readonly string[],
    laterOptimistic: readonly string[],
): string[] {
    const optimisticSet = new Set(optimistic);
    const laterSet = new Set(laterOptimistic);
    return [
        ...current.filter(key => !optimisticSet.has(key) || laterSet.has(key)),
        ...prior.filter(key => !laterSet.has(key)),
    ];
}

export function clearPendingActionItemMutations(): void {
    pendingActionItemMutations.clear();
}
